import {
  type CultureDto,
  MolsonPriceCmdUsersApiService,
  type SetCultureDto
} from '@api/admin'
import { handleCatchError } from '@common/utils'
import { successToast } from '@components/reusable'
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery
} from '@tanstack/react-query'
import { useAuth } from 'react-oidc-context'

export const QUERY_KEYS = {
  USERS: 'users',
  USER: 'user',
  ACCESS_ROLES: 'accessRoles',
  ACCESS_ROLE: 'accessRole',
  USER_DETAILS: 'userDetails',
  USER_SIGNATURE: 'userSignature',
  USER_SETTINGS: 'userSettings',
  APPROVAL_RULES: 'approvalRules',
  APPROVAL_RULE: 'approvalRule',
  APPLICABLE_CONDITIONS: 'applicableConditions',
  ALL_BRANDS: 'allBrands',
  MACHINE_CATEGORIES: 'machineCategories',
  ATTACHMENT_CATEGORIES: 'attachmentCategories',
  BLOB_SOTORAGE_CONTENT: 'fileManagerContent',
  ALL_FEATURE_FLAGS: 'allFeatureFlags',
  ALL_TENANTS: 'allTenants'
} as const

export const useGetUser = (userId: string) => {
  return useSuspenseQuery({
    queryKey: [QUERY_KEYS.USER, userId],
    queryFn: async () => {
      try {
        return await MolsonPriceCmdUsersApiService.getUser(userId)
      } catch (error) {
        // v8 ignore next 2
        handleCatchError(error)
      }
    },
    staleTime: 5 * 60 * 1000
  })
}

const getUsersQueryConfig = (search?: string) => ({
  queryKey: [QUERY_KEYS.USERS, search],
  queryFn: async () => {
    try {
      const response = await MolsonPriceCmdUsersApiService.getUsersList(search)
      return response.filter(user => user.userRole !== '')
    } catch (error) {
      handleCatchError(error)
      return null
    }
  },
  staleTime: 5 * 60 * 1000
})

export const useGetUsers = (enabled = true, search?: string) => {
  return useQuery({
    ...getUsersQueryConfig(search),
    throwOnError: false,
    enabled
  })
}

export const useGetUsersSuspense = (search?: string) => {
  return useSuspenseQuery(getUsersQueryConfig(search))
}

const handleCultureResponseSetup = (culture: CultureDto) => {
  if (
    culture?.currentCulture &&
    culture?.currencySymbol &&
    culture?.dateFormat
  ) {
    localStorage.setItem('preferredRegion', culture?.currentCulture)

    switch (culture?.currentCulture) {
      case 'en-GB':
        localStorage.setItem('preferredCurrencyCode', 'GBP')
        break
      case 'en-US':
        localStorage.setItem('preferredCurrencyCode', 'USD')
        break
      default:
        localStorage.setItem('preferredCurrencyCode', 'GBP')
    }

    localStorage.setItem('preferredCurrencySymbol', culture?.currencySymbol)
    localStorage.setItem('preferredDateFormat', culture?.dateFormat)
  }
}

export const useGetCulture = () => {
  const { isAuthenticated, isLoading } = useAuth()

  return useQuery({
    queryKey: ['culture'],
    queryFn: async () => {
      try {
        const response =
          await MolsonPriceCmdUsersApiService.getCultureSettingsUsersApi()
        if (response.culture) {
          handleCultureResponseSetup(response.culture)
        }
        return response
      } catch (error) {
        handleCatchError(error)
      }
    },
    enabled: isAuthenticated && !isLoading,
    throwOnError: false,
    staleTime: Number.POSITIVE_INFINITY
  })
}

export const useChangeCulture = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (culture: SetCultureDto) => {
      const response =
        await MolsonPriceCmdUsersApiService.setCultureUsersApi(culture)
      if (response.culture) {
        handleCultureResponseSetup(response.culture)
      }
      return response
    },
    onError: error => {
      handleCatchError(error)
    },
    onSuccess: data => {
      // v8 ignore next 2
      if (data) {
        successToast(`${data.message}. Application will reload.`)
        queryClient.invalidateQueries({ queryKey: ['culture'] })
      }
    }
  })
}

export const useGetUserSettingsData = () => {
  return useSuspenseQuery({
    queryKey: [QUERY_KEYS.USER_SETTINGS],
    queryFn: async () => {
      try {
        return await MolsonPriceCmdUsersApiService.userSettingsMy()
      } catch (error) {
        handleCatchError(error)
        return null
      }
    },
    staleTime: 0,
    refetchOnMount: 'always'
  })
}
